/**
 * Rapor içeriği için tek stil/sanitize kaynağı.
 *
 * Amaç: editörde görünen ile raporda görünen birebir aynı olsun. Bunun için
 *  1) aynı CSS kuralları hem TinyMCE içeriğine hem rapor render'ına uygulanır,
 *  2) HTML tek bir yerde temizlenir - editöre yükleme, yapıştırma, sözlük/şablon
 *     metni, editörden kaydetme ve rapor render'ı hep buradan geçer:
 *     font-size / renk / kalın-italik-altı çizili korunur; boşluk, yazı tipi ve
 *     satır aralığı stilleri atılır, satır aralığı DEFAULT_LINE_HEIGHT ile
 *     yeniden yazılır.
 */

// İçerikte korunacak inline style'lar (kullanıcının bilinçli tercihleri).
// line-height bilerek yok: nerede ne ayarlanmis olursa olsun atilir ve
// asagida DEFAULT_LINE_HEIGHT ile yeniden yazilir.
const ALLOWED_STYLES = new Set([
    'color',
    'background-color',
    'font-size',
    'font-weight',
    'font-style',
    'text-decoration',
    'text-decoration-line',
    'text-decoration-style',
    'text-decoration-color',
    'text-align',
    'vertical-align',
    'width',
    'height',
    'border',
    'border-style',
    'border-width',
    'border-color',
    'border-collapse',
]);

/**
 * Satır aralığı tek ve sabit: nerede ne seçilmiş olursa olsun (Word'den gelen
 * %/pt değerler de, editörün line-height butonuyla ayarlananlar da) atılır ve
 * bloklara bu değer yazılır.
 *
 * CSS'e bırakılmıyor, HTML'in içine yazılıyor: böylece dışarı giden içerik
 * (PDF/Word üretimi, HBYS'e kopyalama, HL7) bizim stil dosyamıza bağlı olmadan
 * aynı görünür.
 */
export const DEFAULT_LINE_HEIGHT = '1.5';

// Satır aralığı yazılacak blok elemanlar.
const BLOCK_TAGS = new Set([
    'P', 'DIV', 'LI', 'TD', 'TH', 'BLOCKQUOTE',
    'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
]);

// Koktekı line-height yalnizca yedek: bloklara zaten inline yaziliyor, bu kural
// da henuz kaydedilmemis / blok icermeyen icerigin ayni gorunmesini saglar.
const reportContentRules = (scope) => `
${scope} { color: #000; font-size: 11pt; line-height: ${DEFAULT_LINE_HEIGHT}; }
${scope} p, ${scope} div, ${scope} li, ${scope} td, ${scope} th,
${scope} h1, ${scope} h2, ${scope} h3, ${scope} h4, ${scope} h5, ${scope} h6 { margin: 0; }
${scope} ul, ${scope} ol { margin: 0; padding-left: 24px; }
${scope} table { border-collapse: collapse; }
${scope} td, ${scope} th { padding: 0 2px; vertical-align: top; }
${scope} img { max-width: 100%; }
`;

// TinyMCE iframe'i için (content_style)
export const EDITOR_CONTENT_STYLE = reportContentRules('body');

// Rapor/önizleme ekranları için
export const REPORT_CONTENT_CLASS = 'report-html';

/**
 * HTML'i standartlaştırır: Word/HBYS yapıştırmalarındaki ve eski şablonlardaki
 * margin, font-family, mso-* gibi stilleri temizler, satır aralığını
 * DEFAULT_LINE_HEIGHT'e sabitler; biçim tercihlerini (font-size, renk, kalın...)
 * korur.
 */
export const normalizeReportHtml = (html) => {
    if (!html)
        return '';

    const root = document.createElement('div');
    root.innerHTML = html;
    normalizeReportNode(root);

    return root.innerHTML;
};

/**
 * Aynı temizliği yerinde (DOM üzerinde) yapar.
 * paste_postprocess için gerekli: paste_preprocess'ten sonra TinyMCE kendi
 * dönüşümlerini uyguluyor (örn. convert_fonts_to_spans, <font face> -> inline
 * font-family) ve yeni style'lar üretebiliyor; son söz burada söylenir.
 */
export const normalizeReportNode = (node) => {
    if (!node)
        return;

    for (const element of node.getElementsByTagName('*')) {
        cleanStyle(element);
        cleanClass(element);
        cleanAttributes(element);
        setLineHeight(element);
    }
};

// cleanStyle style attribute'unu bastan yazdigi icin bundan sonra calismali.
const setLineHeight = (element) => {
    if (BLOCK_TAGS.has(element.tagName))
        element.style.setProperty('line-height', DEFAULT_LINE_HEIGHT);
};

const cleanStyle = (element) => {
    if (!element.getAttribute('style'))
        return;

    // element.style shorthand'leri (margin, font, background...) longhand'e açar,
    // bu yüzden tek tek isim kontrolü yeterli.
    const keep = [];
    for (let i = 0; i < element.style.length; i++) {
        const name = element.style[i];
        if (ALLOWED_STYLES.has(name))
            keep.push(`${name}: ${element.style.getPropertyValue(name)}`);
    }

    if (keep.length)
        element.setAttribute('style', keep.join('; '));
    else
        element.removeAttribute('style');
};

// Style dışı, düzeni etkileyen eski HTML nitelikleri.
const cleanAttributes = (element) => {
    if (element.tagName === 'FONT')
        element.removeAttribute('face'); // size/color korunur

    if (element.tagName === 'TABLE') {
        element.removeAttribute('cellpadding');
        element.removeAttribute('cellspacing');
    }
};

const cleanClass = (element) => {
    const className = element.getAttribute('class');
    if (!className)
        return;

    // Word'den gelen Mso* sınıfları kendi aralık/font kurallarını taşıyor.
    const kept = className.split(/\s+/).filter(x => x && !/^mso/i.test(x));
    if (kept.length)
        element.setAttribute('class', kept.join(' '));
    else
        element.removeAttribute('class');
};

let styleInjected = false;
const ensureReportContentStyle = () => {
    if (styleInjected)
        return;

    styleInjected = true;
    const style = document.createElement('style');
    style.setAttribute('data-report-content', '');
    style.innerHTML = reportContentRules(`.${REPORT_CONTENT_CLASS}`);
    document.head.appendChild(style);
};

/**
 * Rapor HTML'ini editördeki ile aynı kurallarla basar.
 * Temizlik burada da yapılır: eski kayıtlardaki inline line-height/margin
 * class kurallarını ezeceği için sadece CSS yeterli olmuyor.
 */
export default function ReportHtml({ html, ...rest }) {
    ensureReportContentStyle();

    return (
        <div
            className={REPORT_CONTENT_CLASS}
            dangerouslySetInnerHTML={{ __html: normalizeReportHtml(html) }}
            {...rest}
        />
    );
}
